import type { ICellRendererParams } from "ag-grid-community";
import {
  formatDate as formatDateUtil,
  formatDateTime as formatDateTimeUtil,
} from "./formatDate";

/** Muted currency next to loan amounts — matches grid text #182737 at ~50% opacity */
const CURRENCY_MUTED_COLOR = "rgba(24, 39, 55, 0.5)";

export const numberFormatter = (params: any, appendCurrency = false) => {
  const value = params.value;
  const currencyCode =
    params.data?.loan_amount_currency ?? params.currency ?? "";

  if (typeof value === "number") {
    if (appendCurrency && currencyCode) {
      return `${value.toLocaleString("en-GB")} ${currencyCode}`;
    }
    return value.toLocaleString("en-GB");
  }
  if (appendCurrency && value !== null && value !== undefined) {
    return currencyCode ? `${value} ${currencyCode}` : String(value);
  }
  return value;
};

/** AG Grid cell: formatted number + de-emphasised currency (pipeline loan columns). */
export function LoanAmountCurrencyCellRenderer(params: ICellRendererParams) {
  const value = params.value;
  const currency =
    (params.data as { loan_amount_currency?: string } | undefined)
      ?.loan_amount_currency ?? (params as { currency?: string }).currency;

  if (value === null || value === undefined || value === "" || value === 0) {
    return null;
  }

  const numberPart =
    typeof value === "number" ? value.toLocaleString("en-GB") : String(value);

  if (!currency) {
    return <>{numberPart}</>;
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: "100%",
        gap: 6,
        minWidth: 0,
      }}
    >
      <span>{numberPart}</span>
      <span style={{ color: CURRENCY_MUTED_COLOR, flexShrink: 0 }}>
        {currency}
      </span>
    </span>
  );
}

export const currencyFormatter = (params: any) => {
  const value = params.value;
  if (value === null || value === undefined) return "-";
  if (typeof value === "number") {
    return new Intl.NumberFormat("en-GB", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }
  return value;
};

export const percentageFormatter = (params: any) => {
  const value = params.value;
  if (value === null || value === undefined) return "-";
  if (typeof value === "number") {
    return `${value}%`;
  }
  return value;
};

/** Normalise any backend date string to HTML date input format (YYYY-MM-DD) for AG Grid date editors. */
export function normaliseToDateInput(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().split("T")[0];
}

// Ag-grid formatter wrapper for formatDate utility
export const dateFormatter = (params: any) => {
  return formatDateUtil(params.value);
};

// Ag-grid formatter wrapper for formatDateTime utility
export const dateTimeFormatter = (params: any) => {
  return formatDateTimeUtil(params.value);
};

export const shortDateFormatter = (params: any) => {
  const value = params.value;
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;

  const day = date.getDate().toString().padStart(2, "0");
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);

  return `${day}-${month}-${year}`; // e.g., 15-Jan-24
};
